import { unstable_createMuiStrictModeTheme as createMuiTheme } from '@material-ui/core/styles'
import color from 'color'

const {
  palette: { augmentColor },
} = createMuiTheme()

const purple = '#3A254C'
const lightPurple = '#D1C8D6'
const fadedPurple = '#F0EBF5'
const orange = '#EA5F28'
const yellow = '#EDD14A'
const darkGreen = '#184C42'
const lightGreen = '#48BA77'
const fadedGreen = '#EEF9F3'
const darkRed = '#CC0000'
const red = '#FF0000'
const white = '#FFFFFF'

const colors = {
  purple: {
    main: purple,
  },
  lightPurple: {
    main: lightPurple,
  },
  fadedPurple: {
    main: fadedPurple,
  },
  orange: {
    main: orange,
  },
  yellow: {
    main: yellow,
  },
  darkGreen: {
    main: darkGreen,
  },
  lightGreen: {
    main: lightGreen,
    contrastText: 'white',
  },
  fadedGreen: {
    main: fadedGreen,
  },
  darkRed: {
    main: darkRed,
  },
  red: {
    main: red,
  },
  white: {
    main: white,
  },
}

const extendedPalette = Object.keys(colors).reduce(
  (palette, colorName) => ({
    ...palette,
    [colorName]: augmentColor(colors[colorName]),
  }),
  {}
)

const spacing = 8

const theme = createMuiTheme({
  maxWidth: 1280,
  breakpoints: {
    unit: 'px',
    values: {
      mobile: 480,
      tablet: 768,
      laptop: 1024,
      desktop: 1280,
      // Required as some mui component use them - eg Snackbar
      xs: 0,
      sm: 768,
      md: 1024,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    background: {
      default: '#FFFFFF',
    },
    primary: colors.purple,
    secondary: colors.orange,
    primaryButton: colors.lightGreen,
    text: {
      primary: colors.purple.main,
      secondary: color(colors.purple.main).desaturate(0.8).lighten(1.2).hex(),
    },
    ...extendedPalette,
  },
  shape: {
    borderRadius: 10,
  },
  spacing,
  typography: {
    fontFamily: ['Work Sans', 'sans-serif'].join(','),
    button: {
      fontFamily: 'Gotham',
    },
    h1: {
      fontSize: '1.65rem',
      fontFamily: 'Gotham',
      fontWeight: 500,
      marginBottom: spacing * 3,
    },
    h2: {
      fontFamily: 'Gotham',
      fontSize: '1.5rem',
      fontWeight: 500,
      marginBottom: spacing * 3,
    },
    h3: {
      fontFamily: 'Gotham',
      fontSize: '1.25rem',
      fontWeight: 'normal',
      marginBottom: spacing,
    },
    h4: {
      fontFamily: 'Gotham',
      fontSize: '1rem',
      fontWeight: 500,
      marginBottom: spacing * 0.5,
      textTransform: 'uppercase',
    },
    h5: {
      fontFamily: 'Gotham',
      fontSize: '0.875rem',
      fontWeight: 500,
      marginBottom: spacing * 0.5,
      textTransform: 'uppercase',
    },
    h6: {
      fontFamily: 'Gotham',
    },
  },
})

theme.overrides = {
  MuiDialogActions: {
    root: {
      padding: theme.spacing(2),
    },
  },
  MuiDialogContent: {
    root: {
      padding: theme.spacing(0, 2),
    },
  },
  MuiDialogTitle: {
    root: {
      padding: theme.spacing(3),
      textAlign: 'center',
    },
  },
  MuiFilledInput: {
    root: {
      borderRadius: theme.shape.borderRadius,
    },
    underline: {
      '&::before, &::after': {
        display: 'none',
      },
    },
  },
  MuiFormControl: {
    marginNormal: {},
  },
  MuiLink: {
    root: {
      color: theme.palette.lightGreen.main,
    },
  },
  MuiTableCell: {
    head: {
      fontFamily: 'Gotham',
      padding: theme.spacing(1, 2),
    },
    root: {
      borderBottom: 'none',
      padding: theme.spacing(1, 2),
    },
  },
  MUIDataTable: {
    tableRoot: {
      backgroundColor: theme.palette.fadedGreen.main,
      borderRadius: theme.shape.borderRadius,
    },
  },
  MUIDataTableHeadCell: {
    fixedHeader: {
      backgroundColor: 'transparent',
    },
    toolButton: {
      fontFamily: 'Gotham',
      color: theme.palette.lightGreen.main,
      textTransform: 'uppercase',
    },
  },
  MUIDataTableFilterList: {
    chip: {
      marginBottom: theme.spacing(2),
    },
  },
  MUIDataTableBodyCell: {
    stackedHeader: {
      fontFamily: 'Gotham',
      fontWeight: 500,
      color: theme.palette.lightGreen.main,
      textTransform: 'uppercase',
    },
  },
  MuiTableRow: {
    hover: {
      cursor: 'pointer',
    },
  },
  MUIDataTableToolbar: {
    root: {
      padding: 0,
      marginBottom: theme.spacing(1),
    },
    titleText: {
      ...theme.typography.h2,
      marginBottom: 0,
    },
  },
  MUIDataTableFilter: {
    gridListTile: {
      minWidth: 125,
    },
  },
  MUIDataTableSelectCell: {
    headerCell: {
      backgroundColor: 'transparent',
    },
  },
}

export default theme
